import { InlineDrawerFrame } from 'views/Agent/form-frame';
import { css, cx } from '@emotion/css';
import styled from '@emotion/styled';
import { Render } from 'components/Render';
import { FormInstance, Drawer, DrawerProps, Button, Form, Alert } from 'antd';
import ForwardFormInstance from 'components/ForwardFormInstance';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RoleIdAccountsMap } from '../components/FormFields/AssignConversationConfigFields';
import RoleDetailForm, { RoleDetailFormProps } from './RoleDetailForm';
import { T, useTranslate } from '@tolgee/react';

export const FooterWrapper = styled.div`
  > button {
    margin-right: 16px;
  }
`;

export interface RoleDetailFormDrawerProps extends Omit<DrawerProps, 'onOk'> {
  formProps: Omit<RoleDetailFormProps, 'form' | 'department' | 'roleIdAccountsMap' | 'getForm'>;
  department: RoleDetailFormProps['department'];
  // 复制并且创建
  onCopyAndCreate?: () => void;
  presentation?: 'sidebar' | 'business';
}

const RoleDetailFormDrawer = (props: RoleDetailFormDrawerProps) => {
  const {
    formProps,
    className,
    department,
    title,
    onClose,
    onCopyAndCreate,
    presentation,
    ...otherProps
  } = props;
  const { t } = useTranslate();
  const Frame = presentation === 'sidebar' ? InlineDrawerFrame : Drawer;
  const formInstRef = useRef<FormInstance<any>>(null);
  const roleIdAccountsMap = useMemo(() => {
    return department.accounts.reduce<RoleIdAccountsMap>((res, account) => {
      const { roleId } = account;

      res[roleId] = [...(res[roleId] || []), account];

      return res;
    }, {});
  }, [department]);

  const [name, setName] = useState(formProps.initialValues?.name);

  const id = formProps.initialValues?.id;

  const drawerTitle = useMemo(() => {
    if (id) {
      if (formProps.disabled) {
        return t('role_privilege_title', { name });
      }

      return t('department.修改x权限', { x: name });
    }

    return title;
  }, [id, title, name, formProps.disabled, t]);

  return (
    <Frame
      width={1200}
      className={cx(
        css`
          .ant-drawer-content-wrapper {
            max-width: 100vw;
          }
        `,
        className,
      )}
      bodyStyle={{ padding: 0 }}
      onClose={onClose}
      footer={
        formProps.disabled ? (
          <FooterWrapper>
            <Button onClick={(e) => onClose && onClose(e as any)}>{t('marketing.关闭')}</Button>
          </FooterWrapper>
        ) : (
          <FooterWrapper>
            <Button
              type="primary"
              onClick={(e) => {
                if (formProps.disabled) {
                  onClose && onClose(e as any);
                } else {
                  formInstRef.current?.submit();
                }
              }}
            >
              {!id ? t('department.确认新建') : t('modals.确认修改')}
            </Button>
            <Button onClick={(e) => onClose && onClose(e as any)}>{t('translation.取消')}</Button>
            {id ? (
              <T
                keyName="account.本次修改权限更新至“x”角色，该角色下有y个帐号"
                params={{
                  x: name,
                  y: roleIdAccountsMap[id]?.length || 0,
                  c: <span style={{ color: 'var(--fett-surface-warning-text)' }} />,
                }}
              />
            ) : null}
          </FooterWrapper>
        )
      }
      {...otherProps}
      title={drawerTitle}
    >
      {formProps?.initialValues?.isDefault ? (
        <Alert
          banner
          message={
            <>
              <span>{t('role_drawer_disabled_banner_msg')}</span>
              <Button
                type="link"
                onClick={() => {
                  onCopyAndCreate?.();
                }}
              >
                {t('department.新建角色')}
              </Button>
            </>
          }
        />
      ) : null}
      <div className="p-6">
        <ForwardFormInstance ref={formInstRef}>
          {(form) => (
            <RoleDetailForm
              department={department}
              roleIdAccountsMap={roleIdAccountsMap}
              {...formProps}
              form={form}
            >
              <Form.Item noStyle shouldUpdate>
                {({ getFieldsValue }) => {
                  return (
                    <Render>
                      {() => {
                        const { name } = getFieldsValue() || {};

                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                          setName(name);
                        }, [name]);

                        return null;
                      }}
                    </Render>
                  );
                }}
              </Form.Item>
            </RoleDetailForm>
          )}
        </ForwardFormInstance>
      </div>
    </Frame>
  );
};
export default RoleDetailFormDrawer;
